import React, { useMemo, useRef, useState, useEffect } from "react";
import { Input } from "@/components/Shared";
import { AlertTriangle } from "lucide-react";

/**
 * MerchantNameAutocomplete — text input with two safeguards against
 * accidentally creating a duplicate merchant under a typo-cased name:
 *
 *   1) Suggestion popover: as you type, a dropdown surfaces existing
 *      distinct merchant names that match (case-insensitive). Each row
 *      shows the canonical spelling + the number of MIDs already on
 *      that name, so you know "Xenn Tech (3 MIDs)" is the established
 *      entity vs. a new one.
 *
 *   2) Casing-conflict banner: if you've typed a name that case-
 *      insensitively matches an existing name BUT the casing differs
 *      (e.g. you typed "xenn tech", existing is "Xenn Tech"), an inline
 *      amber banner appears with a one-click "Use 'Xenn Tech'" button.
 *      You can still override and keep your spelling — but at least
 *      you saw the warning.
 *
 * Props:
 *   - value: current input value
 *   - onChange(v): fires on every keystroke or suggestion click
 *   - existingMerchants: full merchants list from /merchants
 *   - excludeMerchantId: when editing, skip the row being edited so it
 *       doesn't match itself
 *   - testid: forwarded to the Input
 */
export default function MerchantNameAutocomplete({ value, onChange, existingMerchants, excludeMerchantId, testid }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Build the lookup once per merchants list. Key = lowercased name,
  // Value = { canonical: original-cased name, count: number of MIDs }.
  // If two rows have the same name with different casing (e.g. "Xenn
  // Tech" and "xenn tech"), the first one wins as canonical — the
  // banner surfaces this as well so the operator can normalize.
  const nameMap = useMemo(() => {
    const m = new Map();
    for (const row of existingMerchants || []) {
      if (excludeMerchantId && row.merchant_id === excludeMerchantId) continue;
      const raw = (row.merchant_name || "").trim();
      if (!raw) continue;
      const key = raw.toLowerCase();
      const cur = m.get(key);
      if (cur) {
        cur.count += 1;
      } else {
        m.set(key, { canonical: raw, count: 1 });
      }
    }
    return m;
  }, [existingMerchants, excludeMerchantId]);

  // Filter suggestions by the current typed prefix/substring. We include
  // substring matches (not just prefix) so typing "tech" surfaces
  // "Xenn Tech" too — useful when the operator remembers part of the
  // name but not the start.
  const suggestions = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return [];
    const out = [];
    for (const [key, info] of nameMap.entries()) {
      if (key.includes(q)) out.push({ name: info.canonical, count: info.count, exact: key === q });
    }
    // Exact matches first, then alphabetical. Cap at 8 to keep the
    // popover compact — anything longer means the operator hasn't typed
    // enough yet.
    out.sort((a, b) => (b.exact - a.exact) || a.name.localeCompare(b.name));
    return out.slice(0, 8);
  }, [value, nameMap]);

  // Casing-conflict detection: typed value case-insensitively matches an
  // existing name BUT the exact spelling differs. This is the bug the
  // operator is trying to avoid.
  const casingConflict = useMemo(() => {
    const v = (value || "").trim();
    if (!v) return null;
    const hit = nameMap.get(v.toLowerCase());
    if (hit && hit.canonical !== v) return hit;  // case-insensitive match, different casing
    return null;
  }, [value, nameMap]);

  // Close the suggestion popover on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const handleChange = (e) => {
    onChange(e.target.value);
    setOpen(true);
  };

  const pickSuggestion = (name) => {
    onChange(name);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        data-testid={testid}
        value={value}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && !casingConflict && (
        <div
          className="absolute z-30 mt-1 w-full bg-[var(--surface)] border border-[var(--line-2)] rounded-sm shadow-lg max-h-56 overflow-y-auto"
          data-testid={testid ? `${testid}-suggestions` : undefined}
        >
          {suggestions.map((s) => (
            <button
              type="button"
              key={s.name}
              onClick={() => pickSuggestion(s.name)}
              data-testid={testid ? `${testid}-suggestion-${s.name}` : undefined}
              className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-[var(--surface-2)] flex items-center justify-between gap-2"
            >
              <span className="truncate">{s.name}</span>
              <span className="text-[10.5px] text-[var(--muted)] whitespace-nowrap">
                {s.count} MID{s.count === 1 ? "" : "s"}
              </span>
            </button>
          ))}
        </div>
      )}
      {casingConflict && (
        <div
          className="mt-1.5 flex items-start gap-2 px-2.5 py-2 bg-amber-50 border border-amber-200 rounded-sm text-[11.5px] text-amber-900"
          data-testid={testid ? `${testid}-casing-conflict` : undefined}
        >
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <b>"{casingConflict.canonical}"</b> already exists ({casingConflict.count} MID{casingConflict.count === 1 ? "" : "s"}) with different casing.
            Reusing the same spelling keeps dashboards and payouts grouped together.
          </div>
          <button
            type="button"
            data-testid={testid ? `${testid}-use-existing` : undefined}
            onClick={() => onChange(casingConflict.canonical)}
            className="shrink-0 px-2 py-1 text-[11.5px] font-semibold rounded-sm bg-amber-900 text-amber-50 hover:bg-amber-800"
          >
            Use "{casingConflict.canonical}"
          </button>
        </div>
      )}
    </div>
  );
}
